"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHouseholdSensors = void 0;
const react_1 = require("react");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
/**
 * How often to re-reconcile household membership as a backstop for a dropped
 * subscription event. Long on purpose — see the comment at the call site.
 */
const HOUSEHOLD_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Read-side fan-in for a household: the hub this UI runs on plus any gateways
 * linked to it (Hub.parentHubId). Gateways publish under their OWN hubId, so
 * showing their sensors means querying each hub and merging — there is no
 * server-side join.
 *
 * Deliberately separate from `useSensors`, which is single-hub by construction
 * (one state array, hub-filtered subscriptions) and whose `fetchSensorsByHub`
 * CLEARS the whole list on failure. Here each hub is fetched independently, so
 * an unreachable gateway can never blank the primary hub's dashboard.
 */
const useHouseholdSensors = (primaryHubId) => {
    const [sensors, setSensors] = (0, react_1.useState)([]);
    const [gateways, setGateways] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [initialized, setInitialized] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Subscriptions filter creates by household membership through this ref. The
    // server also filters them to the caller's links when they open (KAN-751),
    // so they reopen when the hub set changes (a gateway linked mid-session).
    const hubIdsRef = (0, react_1.useRef)([]);
    const subscriptionsRef = (0, react_1.useRef)([]);
    // Memoized: this array is a dependency of the consumer's context `useMemo`
    // (and of every downstream household-scoped fetch). A fresh literal each
    // render would recompute the AppSync context value on every render and
    // re-render every consumer on the Pi — the exact cost the memoization there
    // exists to avoid.
    const hubIds = (0, react_1.useMemo)(() => (primaryHubId ? [primaryHubId, ...gateways.map((gateway) => gateway.id)] : []), [primaryHubId, gateways]);
    hubIdsRef.current = hubIds;
    // A string, so a gateway refetch returning the same ids doesn't reopen them.
    const hubIdsKey = hubIds.join(',');
    const fetchSensorsForHub = (0, react_1.useCallback)(async (hubId) => {
        const response = await amplifyBootstrap_1.API.graphql({
            query: queries_1.getSensorsByHub,
            variables: { hubId, limit: 100 },
        });
        return response.data?.getSensorsByHub || [];
    }, []);
    const refetch = (0, react_1.useCallback)(async (options) => {
        if (!primaryHubId) {
            setSensors([]);
            setGateways([]);
            return;
        }
        // A background sweep must NOT flip `loading`. Web-hub's Router re-rendered
        // the lock screen continuously once because a periodic refresh toggled a
        // loading flag every couple of minutes — same shape, same trap.
        if (!options?.silent)
            setLoading(true);
        setError(null);
        // Gateway discovery is best-effort: a failure here (or a backend that
        // predates the gateway schema) must still leave the primary hub working.
        let linkedGateways = [];
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getGatewaysByParent,
                variables: { hubId: primaryHubId },
            });
            linkedGateways = response.data?.getGatewaysByParent || [];
        }
        catch (err) {
            console.warn('Gateway discovery failed; showing primary hub only:', err);
        }
        setGateways(linkedGateways);
        const targetHubIds = [primaryHubId, ...linkedGateways.map((gateway) => gateway.id)];
        const results = await Promise.allSettled(targetHubIds.map(fetchSensorsForHub));
        const merged = [];
        const seen = new Set();
        let primaryFailure = null;
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                result.value.forEach((sensor) => {
                    if (!seen.has(sensor.id)) {
                        seen.add(sensor.id);
                        merged.push(sensor);
                    }
                });
                return;
            }
            // Only the primary hub's failure is surfaced as an error — a dead
            // gateway degrades to "its sensors are missing", not a broken screen.
            if (index === 0) {
                primaryFailure = result.reason;
            }
            else {
                console.warn(`Sensor fetch failed for gateway ${targetHubIds[index]}:`, result.reason);
            }
        });
        if (primaryFailure) {
            setError(primaryFailure);
        }
        else {
            setSensors(merged);
        }
        if (!options?.silent)
            setLoading(false);
        setInitialized(true);
    }, [primaryHubId, fetchSensorsForHub]);
    (0, react_1.useEffect)(() => {
        setInitialized(false);
        refetch();
        // Safety net, not the primary path. The subscriptions below are what make a
        // new sensor appear promptly; this only bounds how long a MISSED event can
        // hide one. Before it existed, the list was fetched exactly once per hub id
        // for the life of the page — on the hub kiosk, which runs for days, a sensor
        // that slipped through stayed invisible until someone reloaded the browser.
        // Deliberately slow: the per-category aggregation hooks already poll on 30s
        // for readings, so this only has to catch MEMBERSHIP changes (a sensor added,
        // a gateway linked). Runs silent so it can't flicker consumers' loading UI.
        const timer = setInterval(() => {
            refetch({ silent: true });
        }, HOUSEHOLD_REFETCH_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [refetch]);
    (0, react_1.useEffect)(() => {
        if (!primaryHubId)
            return undefined;
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({ query: subscriptions_1.onCreateSensor }).subscribe({
                next: ({ data }) => {
                    const newSensor = data?.onSensorCreated;
                    if (newSensor && hubIdsRef.current.includes(newSensor.hubId)) {
                        setSensors((prev) => prev.some((sensor) => sensor.id === newSensor.id) ? prev : [...prev, newSensor]);
                    }
                },
                error: (err) => console.error('Household create subscription error:', err),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({ query: subscriptions_1.onUpdateSensor }).subscribe({
                next: ({ data }) => {
                    const updatedSensor = data?.onSensorUpdated;
                    if (updatedSensor) {
                        // Sign-off is write-once server-side (completeSensorInstall), so a
                        // null here only means the triggering mutation didn't select it.
                        // Merging that null used to un-finish installs until the next refetch.
                        setSensors((prev) => prev.map((sensor) => sensor.id === updatedSensor.id
                            ? {
                                ...sensor,
                                ...updatedSensor,
                                installCompletedAt: updatedSensor.installCompletedAt ?? sensor.installCompletedAt,
                            }
                            : sensor));
                    }
                },
                error: (err) => console.error('Household update subscription error:', err),
            });
            const deleteSubscription = amplifyBootstrap_1.API.graphql({ query: subscriptions_1.onDeleteSensor }).subscribe({
                next: ({ data }) => {
                    const deletedSensor = data?.onSensorDeleted;
                    if (deletedSensor) {
                        setSensors((prev) => prev.filter((sensor) => sensor.id !== deletedSensor.id));
                    }
                },
                error: (err) => console.error('Household delete subscription error:', err),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, deleteSubscription];
        }
        catch (err) {
            console.error('Failed to set up household sensor subscriptions:', err);
        }
        return () => {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
            subscriptionsRef.current = [];
        };
    }, [primaryHubId, hubIdsKey]);
    // Memoized for the same reason as `hubIds` — and because several consumers
    // (`useDisplayControls`, `Router`) use it as an effect dependency.
    const ownSensors = (0, react_1.useMemo)(() => (primaryHubId ? sensors.filter((sensor) => sensor.hubId === primaryHubId) : []), [sensors, primaryHubId]);
    return {
        sensors,
        hubIds,
        gateways,
        ownSensors,
        loading,
        initialized,
        error,
        refetch,
    };
};
exports.useHouseholdSensors = useHouseholdSensors;
